import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Button, Modal, Alert } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { QRCodeScanner, ScanResultCard } from '../../components/scanner';
import { apiService } from '../../services/api';
import { AddonScanResult } from '../../types';
import SkeletonLoader from '../../components/common/SkeletonLoader';
import './AddonScannerPage.scss';

const AddonScannerPage: React.FC = () => {
  const { eventSlug } = useParams<{ eventSlug: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [eventTitle, setEventTitle] = useState<string>('');
  const [scanResult, setScanResult] = useState<AddonScanResult | null>(null);
  const [recentScans, setRecentScans] = useState<AddonScanResult[]>([]);
  const [isScanning, setIsScanning] = useState(true);
  const [showResultModal, setShowResultModal] = useState(false);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  
  // Prevent duplicate scans
  const isProcessingRef = useRef(false);
  const lastScannedIdentifierRef = useRef<string | null>(null);
  const lastScanTimeRef = useRef<number>(0);
  const scanCooldownMs = 3000;

  useEffect(() => {
    const fetchEventData = async () => {
      if (!eventSlug) return;

      try {
        setLoading(true);
        const data = await apiService.scanner.getEventScannerData(eventSlug);
        if (data.success && data.event) {
          setEventTitle(data.event.title);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load event data');
      } finally {
        setLoading(false);
      }
    };

    fetchEventData();
  }, [eventSlug]);

  const handleScan = useCallback(async (decodedText: string) => {
    if (!eventSlug || !decodedText) return;

    // Stop scanning immediately
    setIsScanning(false);

    // Prevent duplicate scans
    const now = Date.now();
    const timeSinceLastScan = now - lastScanTimeRef.current;
    
    if (isProcessingRef.current) {
      return;
    }
    
    if (lastScannedIdentifierRef.current === decodedText && timeSinceLastScan < scanCooldownMs) {
      setIsScanning(true);
      return;
    }

    isProcessingRef.current = true;
    lastScannedIdentifierRef.current = decodedText;
    lastScanTimeRef.current = now;

    try {
      const result = await apiService.scanner.scanAddon(eventSlug, decodedText);
      setScanResult(result);
      setShowResultModal(true);
      
      // Add to recent scans if successful
      if (result.success) {
        setRecentScans((prev) => [result, ...prev.slice(0, 4)]);
      }
    } catch (err: any) {
      console.error('Scan error:', err);
      
      let errorMessage = 'Failed to scan addon';
      if (err.response) {
        if (err.response.status === 404) {
          errorMessage = err.response.data?.message || 'Addon order not found. Please check the QR code.';
        } else if (err.response.status === 403) {
          errorMessage = err.response.data?.message || 'You do not have permission to scan this addon.';
        } else {
          errorMessage = err.response.data?.message || err.response.data?.error || `Error: ${err.response.status}`;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      
      if (err.response?.data) {
        setScanResult({
          success: false,
          message: errorMessage,
        } as AddonScanResult);
        setShowResultModal(true);
      } else {
        setTimeout(() => {
          setIsScanning(true);
        }, 1000);
      }
    } finally {
      setTimeout(() => {
        isProcessingRef.current = false;
      }, 500);
    }
  }, [eventSlug, scanCooldownMs]);

  const handleContinueScanning = useCallback(() => {
    setShowResultModal(false);
    setScanResult(null);
    setIsScanning(true);
    setTimeout(() => {
      lastScannedIdentifierRef.current = null;
    }, 1000);
  }, []);

  const handleScanError = (errorMessage: string) => {
    const expectedErrors = [
      'No QR code found',
      'NotFoundException',
      'No MultiFormat Readers were able to detect the code',
      'QR code parse error',
    ];
    
    const isExpectedError = expectedErrors.some(expected => 
      errorMessage && errorMessage.includes(expected)
    );
    
    if (!isExpectedError && errorMessage) {
      console.warn('Scanner error:', errorMessage);
    }
  };

  if (loading) {
    return (
      <div className="addon-scanner-page">
        <Container className="py-2">
          <SkeletonLoader />
        </Container>
      </div>
    );
  }

  return (
    <div className="addon-scanner-page">
      <Container className="py-2">
        {/* Header with Back Button */}
        <div className="scanner-header-wrapper mb-3">
          <div className="scanner-header">
            <button
              className="btn btn-glass btn-icon"
              onClick={() => navigate('/scanner/')}
            >
              <i className="fas fa-arrow-left"></i>
            </button>
            <div className="header-content">
              <div className="header-icon">
                <i className="fas fa-box"></i>
              </div>
              <div className="header-text">
                <h1 className="text-primary mb-0">Addon Scanner</h1>
                <p className="text-muted mb-0 small">{eventTitle}</p>
              </div>
            </div>
            <div className="btn btn-glass invisible">
              <i className="fas fa-arrow-left"></i>
            </div>
          </div>
        </div>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-3">
            {error}
          </Alert>
        )}

        {/* Scanner Container */}
        <div className="scanner-container">
          {/* Main Scanner Section */}
          <div className="scanner-main">
            <div className="scanner-viewport">
              <div className="scan-frame">
                <QRCodeScanner
                  key={facingMode}
                  onScan={handleScan}
                  onError={handleScanError}
                  facingMode={facingMode}
                  paused={!isScanning || !!scanResult}
                />
                {!isScanning && !scanResult && (
                  <div className="scanner-paused text-center d-flex align-items-center justify-content-center">
                    <p className="text-muted">Processing scan...</p>
                  </div>
                )}
              </div>
            </div>
            {/* Camera Controls */}
            <div className="scanner-controls">
              <div className="camera-controls">
                <div className="form-group">
                  <label htmlFor="cam-list" className="mb-2">Select Camera</label>
                  <select
                    id="cam-list"
                    className="form-select"
                    value={facingMode}
                    onChange={(e) => setFacingMode(e.target.value as 'user' | 'environment')}
                  >
                    <option value="environment">Back Camera</option>
                    <option value="user">Front Camera</option>
                  </select>
                </div>
                <button className="btn btn-primary" disabled>
                  <i className="fas fa-bolt"></i>
                  <span>Flash</span>
                </button>
              </div>
            </div>
          </div>

          {/* Side Section (History & Quick Actions for Desktop) */}
          <div className="scanner-side d-none d-lg-flex">
            {/* History Section */}
            <div className="scanner-history">
              <div className="history-header">
                <h5>
                  <i className="fas fa-history me-2"></i>Recent Deliveries
                </h5>
              </div>
              <div className="list-group list-group-flush">
                {recentScans.length === 0 ? (
                  <div className="list-group-item text-center text-muted">
                    <p className="mb-0">No recent deliveries</p>
                  </div>
                ) : (
                  recentScans.map((scan, index) => (
                    <div key={index} className="list-group-item">
                      <h6 className="mb-1">{scan.name}</h6>
                      <p className="mb-1">
                        <small className="text-muted">Qty: {scan.quantity}</small>
                      </p>
                      {scan.purchaser_name && (
                        <p className="mb-1 text-secondary">{scan.purchaser_name}</p>
                      )}
                      {scan.success ? (
                        <span className="badge bg-success">✓ Delivered</span>
                      ) : (
                        <span className="badge bg-danger">✗ Failed</span>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
            {/* Quick Actions */}
            <div className="scanner-instructions">
              <div className="instructions-header">
                <h5>
                  <i className="fas fa-bolt me-2"></i>Quick Actions
                </h5>
              </div>
              <div className="instructions-content">
                <div className="d-grid gap-2">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => navigate(`/scanner/event/${eventSlug}/addon/stats/`)}
                  >
                    <i className="fas fa-chart-bar me-2"></i>View Statistics
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => navigate(`/scanner/event/${eventSlug}/addon/orders/`)}
                  >
                    <i className="fas fa-list me-2"></i>View Orders
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>

      {/* Result Modal */}
      <Modal
        show={showResultModal}
        onHide={handleContinueScanning}
        centered
        size="lg"
        className="result-modal"
      >
        <Modal.Header
          className={scanResult?.success ? 'success' : 'error'}
          style={{
            backgroundColor: scanResult?.success ? 'rgba(25, 135, 84, 1)' : 'rgba(220, 53, 69, 1)',
          }}
        >
          <Modal.Title className="w-100 text-center result-title">
            {scanResult?.success ? 'Success' : 'Failed'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {scanResult && (
            <ScanResultCard result={scanResult} type="addon" />
          )}
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <Button variant="success" onClick={handleContinueScanning} size="sm">
            <i className="fas fa-camera me-2"></i>Continue Scanning
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AddonScannerPage;

